import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Users, Heart, Sparkles } from "lucide-react";
import { handwritten } from "./editorial";

const Mission: React.FC = () => {
  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1400px] mx-auto min-h-screen bg-paper">
      {/* Header */}
      <div className="mb-24 max-w-4xl">
        <div className="flex items-center gap-4 mb-6 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
            Our core mission
          </span>
          <span className="flex-1 h-px bg-ink/15" />
        </div>
        <p
          className="text-2xl md:text-4xl text-salmon -rotate-1 mb-3"
          style={handwritten}
        >
          why we exist
        </p>
        <h1 className="text-6xl md:text-8xl font-serif tracking-tight leading-[0.9] mb-8">
          Urban infrastructure <br />
          <span className="text-salmon italic">for belonging.</span>
        </h1>
        <p className="text-xl md:text-2xl text-gray-600 max-w-2xl leading-relaxed">
          Most adults have no way to bump into the same people more than once.
          LetsLoop is the rebuild around that: a spontaneous community network
          for the things you do anyway.
        </p>
      </div>

      {/* Manifesto Points — Happenings era */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-32 border-t-2 border-ink/10 pt-16">
        <div className="space-y-12">
          <ManifestoItem
            icon={Sparkles}
            number="01"
            title="Plans, not posts"
            text="Other social apps optimise for the post. LetsLoop optimises for the plan. The endpoint is showing up IRL, not scrolling about it."
          />
          <ManifestoItem
            icon={ShieldCheck}
            number="02"
            title="Identity is the trust layer"
            text="Real first name, real photo, real history. Not anonymous, not handle-driven. You see who's already in before you commit, and the host's track record is public."
          />
          <ManifestoItem
            icon={Heart}
            number="03"
            title="Community without the cold start"
            text="Most 'find a community' apps fail because the feed is empty when you arrive. We seed every area with real, recurring loops (volleyball, run clubs, picnics, walks) so day one works."
          />
        </div>

        {/* Intentionally-dark photo card (white text over a dark gradient).
            Pinned to fixed navy in BOTH themes so the dark-mode token remap
            can't flip its ground/overlay light and hide the white caption. */}
        <div className="relative h-full min-h-[500px] rounded-3xl overflow-hidden bg-[#2d3849] group">
          <img
            src="/hero-friends.jpg"
            alt="Friends meeting up at a LetsLoop loop"
            className="absolute inset-0 w-full h-full object-cover opacity-70 grayscale group-hover:grayscale-0 transition-all duration-1000"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2d3849] via-transparent to-transparent opacity-60"></div>
          <div className="absolute inset-0 p-12 flex flex-col justify-end text-white">
            <h3 className="text-3xl font-serif italic mb-4">
              &ldquo;Something&rsquo;s always happening near you.&rdquo;
            </h3>
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-salmon flex items-center justify-center">
                <Users size={12} className="text-ink" />
              </div>
              <p className="text-sm font-medium tracking-wide opacity-80 uppercase">
                The LetsLoop premise
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── What we're building ────────────────────────────────────
          Consumer-only scope: a free, spontaneous IRL community
          network. Future revenue comes from ads, event sponsorships,
          and optional in-app extras — locked in product spec §2. */}
      <div className="mt-32 border-t-2 border-ink/10 pt-16">
        <div className="flex items-center gap-4 mb-6 max-w-md">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-salmon whitespace-nowrap">
            What we&rsquo;re building
          </span>
          <span className="flex-1 h-px bg-ink/15" />
        </div>
        <p
          className="text-2xl md:text-3xl text-salmon -rotate-1 mb-3"
          style={handwritten}
        >
          free to join
        </p>
        <h2 className="text-4xl md:text-6xl font-serif tracking-tight leading-[1.05] mb-8 max-w-4xl">
          The UK&rsquo;s biggest <br />
          <span className="italic text-salmon">
            spontaneous community network.
          </span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 mt-12">
          <div>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              LetsLoop is free for everyone who uses it. The Home feed, the Map,
              posting loops, joining communities, messaging hosts: always free.
              No subscription tier, no paywall.
            </p>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              We&rsquo;re mission-driven. Our goal is to make the city work like
              a small town again, where you bump into the same five people more
              than once a year. Starting in London, expanding to every major UK
              city.
            </p>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
              Revenue arrives later, transparently. Ads, event sponsorships,
              optional in-app extras. None of it gates the core product, none of
              it sells your data. We&rsquo;d rather earn the trust of a hundred
              thousand loopers than monetise the first ten.
            </p>
          </div>

          <div className="space-y-8">
            <RevenuePillar
              label="01"
              title="Free to join"
              body="The Home feed, Map, Create, Communities, and You tab are free today and will stay free as we grow."
            />
            <RevenuePillar
              label="02"
              title="Seeded communities"
              body="Sunday walks, run clubs, book crews, picnic groups across London, seeded by LetsLoop so the feed is never empty on day one."
            />
            <RevenuePillar
              label="03"
              title="UK-wide ambition"
              body="London first. Then Manchester, Bristol, Edinburgh, Leeds. We're building the country's biggest spontaneous community network."
            />
            <RevenuePillar
              label="04"
              title="Transparent monetisation"
              body="When revenue arrives it comes from ads, event sponsorships, and optional in-app extras: never from charging anyone to use the core product, never from selling data."
            />
          </div>
        </div>
      </div>

      {/* Secondary Section: Safety Focus */}
      <div className="mt-32 p-12 md:p-24 rounded-3xl bg-canvas border border-ink/15 shadow-[0_14px_36px_-14px_rgba(5,5,5,0.18)] flex flex-col md:flex-row items-center gap-16">
        <div className="flex-1">
          <h2 className="text-4xl md:text-5xl font-serif mb-6">
            Safety isn&rsquo;t a feature. <br />
            <span className="text-salmon italic text-3xl">
              It&rsquo;s the foundation.
            </span>
          </h2>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="mt-1">
                <ShieldCheck className="text-salmon" size={20} />
              </div>
              <p className="text-gray-600 text-lg">
                <strong>Identity, not handles:</strong> every profile is real:
                first name, photo, and history of what you&rsquo;ve shown up to.
                No anonymous accounts.
              </p>
            </div>
            <div className="flex gap-4">
              <div className="mt-1">
                <Users className="text-salmon" size={20} />
              </div>
              <p className="text-gray-600 text-lg">
                <strong>History you can see:</strong> hover any host&rsquo;s
                name and read their hosting record, attendance rate, and
                community endorsements before you loop in.
              </p>
            </div>
          </div>
        </div>
        <div className="flex-1 w-full max-w-md">
          <div className="relative aspect-square rounded-full border-[1.5px] border-dashed border-gray-300 flex items-center justify-center animate-spin-slow">
            <div className="absolute w-20 h-20 bg-canvas rounded-full border border-gray-100 shadow-xl flex items-center justify-center -top-4">
              <ShieldCheck size={32} className="text-salmon" />
            </div>
            <div className="w-2/3 h-2/3 bg-salmon/5 rounded-full flex items-center justify-center border border-salmon/10">
              <img
                src="/hero-friends.jpg"
                className="w-1/2 h-1/2 rounded-full object-cover border-4 border-white shadow-lg"
                alt="Trusted LetsLoop community"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RevenuePillar = ({
  label,
  title,
  body,
}: {
  label: string;
  title: string;
  body: string;
}) => (
  <div className="flex gap-5">
    <div className="text-sm font-bold text-salmon tracking-widest pt-1">
      {label}
    </div>
    <div>
      <h4 className="text-xl md:text-2xl font-serif tracking-tight mb-2">
        {title}
      </h4>
      <p className="text-base text-gray-600 leading-relaxed">{body}</p>
    </div>
  </div>
);

const ManifestoItem = ({
  number,
  title,
  text,
  icon: Icon,
}: {
  number: string;
  title: string;
  text: string;
  icon: any;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="group"
  >
    <div className="flex items-center gap-4 mb-4">
      <div className="w-10 h-10 rounded-full bg-salmon/10 flex items-center justify-center text-salmon group-hover:bg-salmon group-hover:text-on-salmon transition-all duration-500">
        <Icon size={20} />
      </div>
      <div className="text-sm font-bold text-salmon flex items-center gap-3">
        <span className="w-8 h-[1px] bg-salmon/30"></span>
        {number}
      </div>
    </div>
    <h3 className="text-3xl font-serif tracking-tight mb-4 group-hover:text-salmon transition-colors duration-300">
      {title}
    </h3>
    <p className="text-gray-600 text-lg leading-relaxed">{text}</p>
  </motion.div>
);

export default Mission;
